import React, { Component } from 'react';
import { createReactRenderer } from 'helpers/react-renderer';
import get from 'lodash/get';
import keys from 'lodash/keys';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import { getCountryFromCountryName, getL10nForCountryCode } from 'cirrus/Dropdown/Country/utils';
import { hasRegions, getRegionFromRegionName } from 'cirrus/Dropdown/Region/utils';
import Country from './Country';
import Region from './Region';

const FormRow = ({ children, label, name }) => (
  <tr>
    {label ? (
      <td className="table-address__label label">
        <label htmlFor={name}>{label}</label>
      </td>
    ) : null}
    <td className="table-address__value">{children}</td>
  </tr>
);

const getLocationValue = (rows, key) =>
  get(
    rows.find(o => o[key]),
    key,
    ''
  );

class Address extends Component {
  constructor(props) {
    super(props);

    const { rows } = this.props;

    let countryCode = getLocationValue(rows, 'countryCode');
    let countryName = getLocationValue(rows, 'countryName');
    let regionCode = getLocationValue(rows, 'regionCode');
    let regionName = getLocationValue(rows, 'regionName');

    if (countryName && !countryCode) {
      ({ countryCode, countryName } = getCountryFromCountryName(countryName));
    }

    if (countryCode && hasRegions(countryCode)) {
      ({ regionCode, regionName } = getRegionFromRegionName({
        countryCode,
        regionName: regionCode || regionName,
      }));
    }

    this.state = {
      // eslint-disable-next-line react/no-unused-state
      countryCode,
      // eslint-disable-next-line react/no-unused-state
      countryName,
      regionCode,
      regionName,
      l10nTranslations: getL10nForCountryCode(countryCode).translations,
    };
  }

  handleCountrySelectChange(option) {
    this.handleChanged();

    let { regionCode, regionName } = this.state;
    const { countryCode = '', countryName = '' } = option || {};

    // Reset regionCode on country change
    regionCode = '';

    if (countryCode && hasRegions(countryCode)) {
      ({ regionCode, regionName } = getRegionFromRegionName({
        countryCode,
        regionName: regionCode || regionName,
      }));
    }

    this.setState({
      // eslint-disable-next-line react/no-unused-state
      countryCode,
      // eslint-disable-next-line react/no-unused-state
      countryName,
      regionCode,
      regionName,
      l10nTranslations: getL10nForCountryCode(countryCode).translations,
    });
  }

  handleCountryInputChange(inputValue) {
    this.handleChanged();

    this.setState({
      // eslint-disable-next-line react/no-unused-state
      countryName: inputValue,
    });
  }

  handleRegionSelectChange(option) {
    this.handleChanged();

    const { regionCode = '', regionName = '' } = option || {};

    this.setState({
      regionName,
      regionCode,
    });
  }

  handleRegionInputChange(event) {
    this.handleChanged();

    const { value: regionName } = event.target;

    this.setState({
      regionName,
    });
  }

  handleChanged() {
    if (!this.props.do_saves) return;
    if (!this.isChanged) {
      // Whenever a value is changed from a gui_def form, the save button changes from
      // light gray to blue suggesting to save the current page. Gui_def also saves
      // a `do_save` variable in the window scope so if the user wants to browse
      // away it prompts to save or omit the current changes. Since the Address
      // component is inside gui_def layout the same functions and behaviours are applied.
      window.viewFieldChanged('view');
      this.isChanged = true;
    }
  }

  handleKeyPress(event) {
    if (!this.props.do_saves) return;
    if (
      // On "Enter" key, the gui_def function to save the current form is called.
      window.onEnterKey(event, () => {
        window.saveRecord('view');
      })
    ) {
      // eslint-disable-next-line consistent-return
      return false;
    }
  }

  render() {
    const { rows, tabIndex } = this.props;
    const { l10nTranslations, ...localization } = this.state;

    return (
      <table className="table-address">
        <tbody>
          {rows.map(row => {
            const {
              value,
              name,
              id = name,
              label,
              placeholder,
              className = 'view_view',
              component,
              inputProps,
              ...otherProps
            } = omit(row, keys(localization));

            // eslint-disable-next-line max-len
            const l10nTranslationsPlaceholder =
              l10nTranslations[(placeholder || label).toLowerCase()] || placeholder || label;

            const baseProps = {
              id,
              name,
              className,
              tabIndex,
              placeholder:
                typeof placeholder !== 'undefined' ? placeholder : l10nTranslationsPlaceholder,
            };
            const localizationProps = pick(localization, keys(row));

            return (
              <FormRow label={label && l10nTranslationsPlaceholder} name={name} key={name}>
                {component ? (
                  <div className="cirrus">
                    {component === 'Country' ? (
                      <Country
                        dropdownClassName="cr-autocomplete"
                        {...baseProps}
                        onSelectChange={option => this.handleCountrySelectChange(option)}
                        onInputChange={option => this.handleCountryInputChange(option)}
                        inputProps={inputProps}
                        {...localizationProps}
                        {...otherProps}
                      />
                    ) : null}
                    {component === 'Region' ? (
                      <Region
                        dropdownClassName="cr-autocomplete"
                        {...baseProps}
                        onSelectChange={option => this.handleRegionSelectChange(option)}
                        onChange={option => this.handleRegionInputChange(option)}
                        onKeyPress={option => this.handleKeyPress(option)}
                        inputProps={inputProps}
                        {...localizationProps}
                        {...otherProps}
                      />
                    ) : null}
                  </div>
                ) : (
                  <input
                    {...baseProps}
                    type="text"
                    autoComplete="off"
                    defaultValue={value}
                    size="30"
                    maxLength="255"
                    onChange={option => this.handleChanged(option)}
                    onKeyPress={option => this.handleKeyPress(option)}
                    style={{ height: '32px', lineHeight: '32px' }}
                    {...otherProps}
                  />
                )}
              </FormRow>
            );
          })}
        </tbody>
      </table>
    );
  }
}

const RenderedComponent = createReactRenderer({
  component: Address,
  selector: '[data-react-root="Address"]',
  callback: () => {
    // Toggle continer visibility on register page to fix an async loading issue explained below
    if (window.location.pathname === '/register.php') {
      const shipToWrapper = document.getElementById('ship_to_data');
      if (shipToWrapper instanceof HTMLElement) {
        document.getElementById('ship_to_data').style.display = 'block';
        document.getElementById('ship_to_data_loading').style.display = 'none';
      }
    }
  },
});

history.onChangeState(
  [
    {
      name: [
        'customer.views.customer',
        'customer.views.register_customer',
        'admin.views.shop',
        'admin.views.employee',
        'credit_account.views.credit_account',
        'vendor.views.vendor',
        'ship_to.views.ship_to',
      ],
      id: undefined,
    },
    { pathname: '/register.php', form_name: undefined },
    { name: 'workbench.views.beta_workorder', tab: 'details' },
    { name: 'transaction.views.transaction', tab: 'ship_to', id: undefined },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default Address;
